import React from 'react';
import { View, TouchableOpacity } from 'react-native';

import {
  VFInput,
  VFButton,
  PasswordInput,
  Toggle,
  ToggleSize,
  VFText,
} from '@vfgroup-oneplatform/foundation/Components';
import { withTheme, LightThemeColors } from '@vfgroup-oneplatform/foundation/Components/Themes';
import { ViewPropTypes } from 'deprecated-react-native-prop-types';
import { isFunction as _isFunction } from 'lodash';
import PropTypes from 'prop-types';

import { Images } from '../../../Themes';
import LoginTestKeys from '../../../Utils/LoginTestKeys';

import styles from './LoginForm.Styles';

function LoginForm({
  password,
  rememberMe,
  loginDisabled,
  isValid,
  theme,
  showErrorBox,
  subErrorMessage,
  showRememberMe,
  emailOrPhone,
  showErrorBoxNotvalid,
  showSecondaryButton,
  secondaryButtonProps,
  emailOrPhoneRef,
  validMessage,
  onEmailOrPhoneChange,
  passwordRef,
  onPasswordChange,
  onOptionsViewLayout,
  onRememberMeViewLayout,
  onRememberMeChange,
  forgetPasswordWidth,
  getForgetPasswordStyleMemoized,
  forgotPasswordContainerStyle,
  forgotPasswordTextStyle,
  forgottenPassword,
  onLoginPress,
  keyboardType,
  userNameInputLabel,
  passwordInputLabel,
}) {
  return (
    <View style={styles.containerStyle}>
      <VFInput
        label={userNameInputLabel}
        leftIconName={Images.ic_mobile}
        baseColor={
          isValid
            ? theme.colors.textInput.default.baseColor
            : theme.colors.textInput.error.baseColor
        }
        tintColor={
          isValid
            ? theme.colors.textInput.default.tintColor
            : theme.colors.textInput.error.tintColor
        }
        selectionColor={theme.colors.textInput.default.selectionColor}
        textColor={theme.colors.textColor}
        labelTextStyle={styles.labelTextStyle}
        value={emailOrPhone}
        fieldRef={emailOrPhoneRef}
        onChangeText={onEmailOrPhoneChange}
        helperText={validMessage}
        showHelperText={!isValid}
        isValid={isValid}
        helperTextColor={
          showErrorBoxNotvalid
            ? theme.colors.textInput.error.tintColor
            : theme.colors.textInput.default.tintColor
        }
        inputAccessibilityLabel={LoginTestKeys.loginEmailTxtInput}
        testID={LoginTestKeys.loginEmailTxtInput}
        childrenContainerAccessibilityLabel={LoginTestKeys.loginEmail}
        helperAccessibilityLabel={LoginTestKeys.helperText}
        autoCapitalize="none"
        keyboardType={keyboardType ?? 'default'}
        containerTestID={LoginTestKeys.loginByEmailText}
      />

      <PasswordInput
        label={passwordInputLabel}
        value={password}
        fieldRef={passwordRef}
        onChangeText={onPasswordChange}
        baseColor={theme.colors.textInput.default.baseColor}
        tintColor={theme.colors.textInput.default.tintColor}
        selectionColor={theme.colors.textInput.default.selectionColor}
        textColor={theme.colors.textColor}
        labelTextStyle={styles.labelTextStyle}
        containerStyle={styles.passwordInputStyle}
        inputAccessibilityLabel={LoginTestKeys.loginPassTxtInput}
        testID={LoginTestKeys.loginPassTxtInput}
        childrenContainerAccessibilityLabel={LoginTestKeys.loginPassword}
        rightIconAccessibilityLabel={LoginTestKeys.passInputRightIcon}
        helperText={subErrorMessage}
        showHelperText={showErrorBox && subErrorMessage}
        helperTextColor={
          showErrorBoxNotvalid
            ? theme.colors.textInput.error.tintColor
            : theme.colors.textInput.default.tintColor
        }
        helperAccessibilityLabel={LoginTestKeys.passInputError}
        containerTestID={LoginTestKeys.loginByEmailText}
        rigthIconTestID={LoginTestKeys.loginByEmailIcon}
        onKeyPress={(e) => {
          if (e.nativeEvent.key === 'Enter') {
            onLoginPress()
          }
        }}
      />

      <View
        style={styles.logInOptionsContainerStyle(showRememberMe)}
        onLayout={onOptionsViewLayout}
      >
        {showRememberMe && (
          <View style={styles.toggleContainerStyle} onLayout={onRememberMeViewLayout}>
            <Toggle
              inactiveContainerStyle={styles.toggleStyle}
              initialValue={rememberMe}
              size={ToggleSize.SMALL}
              onChange={onRememberMeChange}
              accessibilityLabel={LoginTestKeys.loginRememberToggle}
              testID={LoginTestKeys.loginRememberToggle}
            />
            <VFText
              i18nKey="upfront_remember_me_label"
              style={styles.rememberMeTextStyle(theme)}
              accessibilityLabel={LoginTestKeys.loginRememberMeTxt}
              testID={LoginTestKeys.loginRememberMeTxt}
            />
          </View>
        )}

        {_isFunction(forgottenPassword) && (
          <TouchableOpacity
            style={[
              styles.forgetBtn,
              getForgetPasswordStyleMemoized(forgetPasswordWidth),
              forgotPasswordContainerStyle,
            ]}
            onPress={forgottenPassword}
            testID={LoginTestKeys.loginForgetYourPassLink}
          >
            <VFText
              i18nKey="upfront_forgot_your_password_label"
              numberOfLines={2}
              ellipsizeMode={'tail'}
              style={[styles.textStyle(theme), forgotPasswordTextStyle]}
              accessibilityLabel={LoginTestKeys.loginForgetYourPassLinkText}
            />
          </TouchableOpacity>
        )}
      </View>

      <VFButton
        title="upfront_login_text"
        style={styles.buttonStyle}
        textStyle={styles.buttonTextStyle}
        disabled={loginDisabled}
        onPress={onLoginPress}
        testID={LoginTestKeys.loginLoginButton}
        textAccessibilityLabel={LoginTestKeys.loginLoginButton}
        disabledStyle={styles.disabledBUttonStyle(theme)}
        disabledTextStyle={styles.disabledTextStyle(theme)}
        type="primary"
      />
      {showSecondaryButton && (
        <>
          <VFText i18nKey={'upfront_login_or_text'} style={styles.orText(theme)} />
          <VFButton
            {...secondaryButtonProps}
            style={[styles.secondaryButtonStyle, secondaryButtonProps.style]}
          />
        </>
      )}
    </View>
  );
}

LoginForm.defaultProps = {
  theme: LightThemeColors,
  userNameInputLabel: 'upfront_email_or_phone_number_hint',
  passwordInputLabel: 'upfront_password_hint',
};

LoginForm.propTypes = {
  password: PropTypes.string,
  rememberMe: PropTypes.bool,
  loginDisabled: PropTypes.bool,
  forgetPasswordWidth: PropTypes.number,
  isValid: PropTypes.bool,
  theme: PropTypes.shape({
    name: PropTypes.string,
    isDark: PropTypes.bool,
    colors: PropTypes.object,
  }),
  showErrorBox: PropTypes.bool,
  subErrorMessage: PropTypes.string,
  showRememberMe: PropTypes.bool,
  emailOrPhone: PropTypes.string,
  showErrorBoxNotvalid: PropTypes.bool,
  showSecondaryButton: PropTypes.bool,
  secondaryButtonProps: PropTypes.object,
  emailOrPhoneRef: PropTypes.object,
  validMessage: PropTypes.string,
  onEmailOrPhoneChange: PropTypes.func,
  passwordRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  onPasswordChange: PropTypes.func,
  onOptionsViewLayout: PropTypes.func,
  onRememberMeViewLayout: PropTypes.func,
  onRememberMeChange: PropTypes.func,
  forgotPasswordContainerStyle: ViewPropTypes.style,
  forgotPasswordTextStyle: ViewPropTypes.style,
  forgottenPassword: PropTypes.func,
  onLoginPress: PropTypes.func,
  getForgetPasswordStyleMemoized: PropTypes.func,
  keyboardType: PropTypes.string,
  userNameInputLabel: PropTypes.string,
  passwordInputLabel: PropTypes.string,
};

export default withTheme(LoginForm);
